import { useState, useEffect, lazy, Suspense } from 'react';
import { Download } from 'lucide-react';

import { logTabSwitch, logLocationSwitch, logDeepLinkOpen } from '../utils/analytics';
import { useLiveBoard } from '../hooks/useLiveBoard';
import { useIsHoliday } from '../hooks/useIsHoliday';
import { useCauseList } from '../hooks/useCauseList';
import { usePlayStorePromo, getPlayStoreUrl } from '../hooks/usePlayStorePromo';
import { LocationTabs } from '../components/LocationTabs';
import { CourtGrid } from '../components/CourtGrid';
import { LastUpdatedBar } from '../components/LastUpdatedBar';
import { AnnouncementsBanner } from '../components/AnnouncementsBanner';
import { CauseListSeoBlock } from '../components/CauseListSeoBlock';
import { PlayStoreTopBanner, GooglePlayIcon } from '../components/PlayStoreTopBanner';
import { useAndroidBackHandler } from '../hooks/useAndroidBackHandler';

import { initNativePushNotifications } from '../utils/notifications';
import { openExternalUrl } from '../utils/openExternalUrl';
import { trackSession, trackEngagement } from '../utils/inAppReview';
import { checkForAppUpdates } from '../utils/inAppUpdate';
import type { Location, LiveStatusDoc } from '../types';

const CaseDetailsSheet = lazy(() => import('../components/CaseDetailsSheet').then(m => ({ default: m.CaseDetailsSheet })));
const CauseListTab = lazy(() => import('./CauseListTab').then(m => ({ default: m.CauseListTab })));
const DailyOrdersTab = lazy(() => import('./DailyOrdersTab').then(m => ({ default: m.DailyOrdersTab })));
const PlayStorePromoDialog = lazy(() => import('../components/PlayStorePromoDialog').then(m => ({ default: m.PlayStorePromoDialog })));

type ViewMode = 'live' | 'causelist' | 'orders';

export function LiveBoard() {
  const [location, setLocation] = useState<Location>(() => {
    const params = new URLSearchParams(window.location.search);
    const loc = params.get('location');
    return (loc === 'madurai' || loc === 'madras') ? loc : 'madras';
  });

  const [view, setView] = useState<ViewMode>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('case') ? 'causelist' : 'live';
  });
  const [deepLinkedCase] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('case');
    if (!raw) return null;
    const caseNumber = raw.replace(/~/g, '/');
    const loc = params.get('location') ?? 'madras';
    logDeepLinkOpen(caseNumber, loc);
    return caseNumber;
  });
  const [selectedCourt, setSelectedCourt] = useState<LiveStatusDoc | null>(null);

  const {
    isOpen: isPromoOpen,
    isBannerVisible,
    isNative,
    dismiss: dismissPromo,
    dismissBanner,
    openPlayStore,
    openManual: openPromoManual,
  } = usePlayStorePromo();


  const { courts, loading, error } = useLiveBoard(location);
  const isHoliday = useIsHoliday();
  const { meta } = useCauseList(location);
  const announcements = meta?.rawAnnouncements || null;
  const causeListDate = meta?.date || null;

  const handleDeepLink = (rawUrl: string) => {
    try {
      const url = new URL(rawUrl);
      const loc = url.searchParams.get('location') as Location | null;
      if (loc && (loc === 'madras' || loc === 'madurai')) {
        setLocation(loc);
      }
      if (url.searchParams.has('case')) {
        setView('causelist');
      }
      const courtParam = url.searchParams.get('court');
      if (courtParam) {
        const found = courts.find(c => c.court === courtParam);
        if (found) setSelectedCourt(found);
      }
    } catch (e) {
      console.warn('[DeepLink] parse failed:', e);
    }
  };

  useAndroidBackHandler({
    isModalOrSheetOpen: selectedCourt !== null || isPromoOpen,
    onCloseModalOrSheet: () => {
      if (isPromoOpen) {
        dismissPromo('close_btn');
      } else {
        setSelectedCourt(null);
      }
    },
    view,
    onGoHome: () => setView('live'),
    onDeepLink: handleDeepLink,
  });

  useEffect(() => {
    trackSession();
    checkForAppUpdates();
  }, []);

  useEffect(() => {
    initNativePushNotifications((data) => {
      if (data.url && typeof data.url === 'string') {
        handleDeepLink(data.url);
      } else if (data.caseNumber) {
        setView('causelist');
        if (data.location && (data.location === 'madras' || data.location === 'madurai')) {
          setLocation(data.location as Location);
        }
      }
    });
  }, [courts]);

  useEffect(() => {
    if (!loading && courts.length > 0) {
      const params = new URLSearchParams(window.location.search);
      const targetCourt = params.get('court');
      const targetLocation = params.get('location');
      const showDetails = params.get('details');

      if (targetCourt && targetLocation === location) {
        const elId = `court-card-${targetLocation}-${targetCourt}`;
        const el = document.getElementById(elId);
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (!showDetails) {
              el.classList.add('court-card--highlight');
              setTimeout(() => el.classList.remove('court-card--highlight'), 3000);
            }
          }, 100);
        }

        if (showDetails) {
          const courtDoc = courts.find(c => c.court === targetCourt);
          if (courtDoc) setSelectedCourt(courtDoc);
        }
      }
    }
  }, [loading, courts, location]);

  const latestPoll = courts.length > 0
    ? Math.max(...courts.map((c) => c.polledAt))
    : null;

  const formattedDate = causeListDate ? `${causeListDate.substring(6,8)}${causeListDate.substring(4,6)}${causeListDate.substring(0,4)}` : '';
  // Fallback guess (lowercase "cause_") is used only until the backend has resolved and
  // stored the actual casing ("cause_" vs "CAUSE_") that the court published under — see
  // ingestAnnouncements.ts resolvePdfUrl().
  const pdfUrl = meta?.pdfUrl ?? (causeListDate ? `https://mhc.tn.gov.in/judis/clists/clists-${location}/causelists/pdf/cause_${formattedDate}.pdf` : null);

  const liveCount = courts.filter(c => c.status === 'live').length;
  const doneCount = courts.filter(c => c.status === 'list_over').length;

  return (
    <main className="live-board">
      {!isNative && isBannerVisible && (
        <PlayStoreTopBanner
          onOpenPromo={openPromoManual}
          onDismiss={dismissBanner}
        />
      )}
      <header className="live-board__header">

        <div className="live-board__title-row">
          <img src="/icon-96.png" srcSet="/icon-48.png 1x, /icon-96.png 2x" width="40" height="40" alt="Court Pulse" className="live-board__logo" />
          <div className="live-board__title-group">
            <h1 className="live-board__title">Court Pulse</h1>
            <span className="live-board__subtitle">Madras High Court — Live</span>
          </div>
        </div>
        {view === 'live' && <LastUpdatedBar polledAt={latestPoll} isHoliday={isHoliday} />}
      </header>

      <div className="live-board__controls">
        <LocationTabs selected={location} onChange={(loc) => { logLocationSwitch(loc); setLocation(loc); }} />
        <div className="live-board__controls-right">
          {!isNative && (
            <button
              type="button"
              className="app-promo-header-pill"
              onClick={openPromoManual}
              title="Get MHC Court Pulse for Android"
              aria-label="Download Android App on Google Play"
            >
              <GooglePlayIcon size={14} />
              <span>Get App</span>
            </button>
          )}

          {view === 'live' && !loading && (liveCount > 0 || doneCount > 0) && (
            <div className="live-board__pills">
              {liveCount > 0 && <span className="court-grid__pill court-grid__pill--live">{liveCount} live</span>}
              {doneCount > 0 && <span className="court-grid__pill court-grid__pill--done">{doneCount} list over</span>}
            </div>
          )}
        </div>
      </div>

      <div className="view-tabs">
        <button
          className={`view-tab ${view === 'live' ? 'view-tab--active' : ''}`}
          onClick={() => { logTabSwitch('live_board'); setView('live'); }}
        >
          Live Board
        </button>
        <button
          className={`view-tab ${view === 'causelist' ? 'view-tab--active' : ''}`}
          onClick={() => { logTabSwitch('cause_list'); setView('causelist'); }}
        >
          Cause List
        </button>
        <button
          className={`view-tab ${view === 'orders' ? 'view-tab--active' : ''}`}
          onClick={() => { logTabSwitch('daily_orders'); setView('orders'); }}
        >
          Daily Orders
        </button>
        {pdfUrl && view === 'causelist' && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              openExternalUrl(pdfUrl);
            }}
            className="cause-list-dl-btn cause-list-dl-btn--icon"
            aria-label="Download Cause List PDF"
          >
            <Download size={15} />
          </a>
        )}
      </div>

      <AnnouncementsBanner rawAnnouncements={announcements} />

      {view === 'live' && (
        <>
          {error && (
            <div className="live-board__error">
              Failed to connect to live board. Retrying...
            </div>
          )}
          <CourtGrid
            courts={courts}
            loading={loading}
            onSelectCourt={(c) => {
              trackEngagement(1);
              setSelectedCourt(c);
            }}
          />
          {selectedCourt && (
            <Suspense fallback={null}>
              <CaseDetailsSheet
                court={courts.find(c => c.court === selectedCourt.court) || selectedCourt}
                onClose={() => setSelectedCourt(null)}
                isSample={selectedCourt.id === 'sample_madras_7'}
              />
            </Suspense>
          )}
        </>
      )}

      {view === 'causelist' && (
        <Suspense fallback={<div className="cl-tab__empty">Loading cause list…</div>}>
          <CauseListTab location={location} initialCaseNumber={deepLinkedCase ?? undefined} />
        </Suspense>
      )}

      {view === 'orders' && (
        <Suspense fallback={<div className="cl-tab__empty">Loading daily orders…</div>}>
          <DailyOrdersTab location={location} />
        </Suspense>
      )}

      <CauseListSeoBlock />

      <footer style={{
        marginTop: 'auto',
        padding: '24px 16px',
        textAlign: 'center',
        fontSize: '0.75rem',
        color: '#64748b',
        borderTop: '1px solid #e2e8f0',
        lineHeight: '1.5',
        backgroundColor: '#f8fafc',
        marginInline: '-20px'
      }}>
        <div style={{ marginBottom: '12px' }}>
          <strong>Disclaimer:</strong> MHC Court Pulse is an independent, unofficial tool. We do not guarantee the accuracy or real-time precision of the data. For authoritative court statuses, please refer exclusively to the official <a href="https://mhc.tn.gov.in/" target="_blank" rel="noopener noreferrer" style={{color: 'inherit', textDecoration: 'underline'}}>Madras High Court website</a>.
        </div>
        <div>
          Browse all courts: <a href="/courts/madras" style={{color: 'inherit', textDecoration: 'underline'}}>Madras (Principal Seat)</a> &middot; <a href="/courts/madurai" style={{color: 'inherit', textDecoration: 'underline'}}>Madurai Bench</a> &middot; <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{color: 'inherit', textDecoration: 'underline'}}>Privacy Policy</a> &middot; <a href={getPlayStoreUrl('footer_link')} target="_blank" rel="noopener noreferrer" style={{color: 'inherit', textDecoration: 'underline'}} onClick={() => openPlayStore('footer_link')}>Android App</a>
        </div>
      </footer>

      {!isNative && (
        <Suspense fallback={null}>
          <PlayStorePromoDialog
            isOpen={isPromoOpen}
            onDismiss={dismissPromo}
            onGetApp={openPlayStore}
          />
        </Suspense>
      )}
    </main>
  );
}
